/**
 * Core Visual Feedback System
 * 
 * Provides comprehensive visual feedback for field formatting operations,
 * including validation states, error indicators, and user feedback for
 * invalid input scenarios with accessible design.
 */

import { ValidationResult } from '../types/formatting-types';
import { FieldType } from '../detection-patterns';
import { FieldInfo } from '../field-registry';
import { ErrorMessages } from './error-messages';
import { AccessibilityManager } from './accessibility';
import { FeedbackAnimations } from './animations';
import { FeedbackStyles } from '../styles/feedback-styles';

/**
 * Visual feedback states
 */
export enum FeedbackState {
  DEFAULT = 'default',
  VALID = 'valid',
  INVALID = 'invalid',
  WARNING = 'warning',
  FOCUS = 'focus',
  LOADING = 'loading'
}

/**
 * Visual feedback element context
 */
export interface FeedbackContext {
  /** The field information */
  field: FieldInfo;
  /** Current validation result */
  validation?: ValidationResult;
  /** Current feedback state */
  state: FeedbackState;
  /** Error message element (if exists) */
  messageElement?: HTMLElement;
  /** Error icon element (if exists) */
  iconElement?: HTMLElement;
  /** Whether accessibility features are enabled */
  accessibilityEnabled: boolean;
  /** Last state change timestamp */
  lastStateChange: number;
}

/**
 * Visual feedback configuration
 */
export interface FeedbackConfig {
  /** Enable/disable real-time validation feedback */
  enableRealTimeFeedback?: boolean;
  /** Enable/disable success indicators */
  enableSuccessIndicators?: boolean;
  /** Enable/disable warning states */
  enableWarningStates?: boolean;
  /** Enable/disable loading states */
  enableLoadingStates?: boolean;
  /** Enable/disable animations */
  enableAnimations?: boolean;
  /** Enable/disable accessibility features */
  enableAccessibility?: boolean;
  /** Custom CSS class prefix */
  cssClassPrefix?: string;
  /** Position for error messages */
  messagePosition?: 'below' | 'above' | 'inline' | 'tooltip';
  /** Maximum message display duration (0 = persistent) */
  messageTimeout?: number;
}

/**
 * Default feedback configuration
 */
const DEFAULT_FEEDBACK_CONFIG: Required<FeedbackConfig> = {
  enableRealTimeFeedback: true,
  enableSuccessIndicators: false, // Optional, non-intrusive
  enableWarningStates: true,
  enableLoadingStates: true,
  enableAnimations: true,
  enableAccessibility: true,
  cssClassPrefix: 'formatting',
  messagePosition: 'below',
  messageTimeout: 0 // Persistent by default
};

/**
 * Core visual feedback system
 */
export class VisualFeedback {
  private config: Required<FeedbackConfig>;
  private contexts = new Map<HTMLElement, FeedbackContext>();
  private errorMessages: ErrorMessages;
  private accessibilityManager: AccessibilityManager;
  private animations: FeedbackAnimations;
  private styles: FeedbackStyles;

  constructor(config: FeedbackConfig = {}) {
    this.config = { ...DEFAULT_FEEDBACK_CONFIG, ...config };
    this.errorMessages = new ErrorMessages();
    this.accessibilityManager = new AccessibilityManager(this.config.enableAccessibility);
    this.animations = new FeedbackAnimations(this.config.enableAnimations);
    this.styles = new FeedbackStyles(this.config.cssClassPrefix);

    // Initialize styles in DOM
    this.initializeStyles();
  }

  /**
   * Initialize a field for visual feedback
   */
  public initializeField(field: FieldInfo): void {
    const context: FeedbackContext = {
      field,
      state: FeedbackState.DEFAULT,
      accessibilityEnabled: this.config.enableAccessibility,
      lastStateChange: Date.now()
    };

    this.contexts.set(field.element, context);

    // Set up accessibility attributes
    if (this.config.enableAccessibility) {
      this.accessibilityManager.setupField(field.element, field.fieldType);
    }

    // Apply default styling
    this.applyStateStyles(field.element, FeedbackState.DEFAULT);
  }

  /**
   * Show error state with validation feedback
   */
  public showError(field: FieldInfo, validation: ValidationResult): void {
    const context = this.contexts.get(field.element);
    if (!context) {
      console.warn('VisualFeedback: Field not initialized', field.element);
      return;
    }

    // Update context
    context.validation = validation;
    context.state = FeedbackState.INVALID;
    context.lastStateChange = Date.now();

    // Apply error styling
    this.applyStateStyles(field.element, FeedbackState.INVALID);

    // Show error message
    this.showErrorMessage(context, validation.errors);

    // Show error icon
    this.showErrorIcon(context);

    // Apply animations
    if (this.config.enableAnimations) {
      this.animations.playErrorAnimation(field.element);
    }

    // Update accessibility
    if (this.config.enableAccessibility) {
      this.accessibilityManager.announceValidationError(field.element, validation.errors);
    }
  }

  /**
   * Show valid state
   */
  public showValid(field: FieldInfo): void {
    if (!this.config.enableSuccessIndicators) {
      this.clearFeedback(field);
      return;
    }

    const context = this.contexts.get(field.element);
    if (!context) {
      console.warn('VisualFeedback: Field not initialized', field.element);
      return;
    }

    // Update context
    context.validation = { isValid: true, errors: [], warnings: [] };
    context.state = FeedbackState.VALID;
    context.lastStateChange = Date.now();

    // Apply valid styling
    this.applyStateStyles(field.element, FeedbackState.VALID);

    // Clear any error messages
    this.clearErrorMessage(context);
    this.clearErrorIcon(context);

    // Apply animations
    if (this.config.enableAnimations) {
      this.animations.playSuccessAnimation(field.element);
    }

    // Update accessibility
    if (this.config.enableAccessibility) {
      this.accessibilityManager.announceValidationSuccess(field.element);
    }
  }

  /**
   * Show warning state
   */
  public showWarning(field: FieldInfo, warnings: string[]): void {
    if (!this.config.enableWarningStates) {
      return;
    }

    const context = this.contexts.get(field.element);
    if (!context) {
      console.warn('VisualFeedback: Field not initialized', field.element);
      return;
    }

    // Update context
    context.validation = { isValid: true, errors: [], warnings };
    context.state = FeedbackState.WARNING;
    context.lastStateChange = Date.now();

    // Apply warning styling
    this.applyStateStyles(field.element, FeedbackState.WARNING);

    // Show warning message
    this.showWarningMessage(context, warnings);

    // Update accessibility
    if (this.config.enableAccessibility) {
      this.accessibilityManager.announceWarning(field.element, warnings);
    }
  }

  /**
   * Show loading state
   */
  public showLoading(field: FieldInfo): void {
    if (!this.config.enableLoadingStates) {
      return;
    }

    const context = this.contexts.get(field.element);
    if (!context) {
      console.warn('VisualFeedback: Field not initialized', field.element);
      return;
    }

    // Update context
    context.state = FeedbackState.LOADING;
    context.lastStateChange = Date.now();

    // Apply loading styling
    this.applyStateStyles(field.element, FeedbackState.LOADING);

    // Apply animations
    if (this.config.enableAnimations) {
      this.animations.playLoadingAnimation(field.element);
    }
  }

  /**
   * Handle focus state
   */
  public handleFocus(field: FieldInfo): void {
    const context = this.contexts.get(field.element);
    if (!context) {
      return;
    }

    // Don't override error states on focus
    if (context.state === FeedbackState.INVALID) {
      return;
    }

    // Apply focus styling
    this.applyStateStyles(field.element, FeedbackState.FOCUS);

    // Clear any messages that might interfere with input
    if (context.state as string !== FeedbackState.INVALID) {
      this.clearErrorMessage(context);
    }
  }

  /**
   * Clear all feedback states
   */
  public clearFeedback(field: FieldInfo): void {
    const context = this.contexts.get(field.element);
    if (!context) {
      return;
    }

    // Update context
    context.validation = undefined;
    context.state = FeedbackState.DEFAULT;
    context.lastStateChange = Date.now();

    // Apply default styling
    this.applyStateStyles(field.element, FeedbackState.DEFAULT);

    // Clear messages and icons
    this.clearErrorMessage(context);
    this.clearErrorIcon(context);

    // Clear animations
    if (this.config.enableAnimations) {
      this.animations.clearAnimations(field.element);
    }

    // Update accessibility
    if (this.config.enableAccessibility) {
      this.accessibilityManager.clearValidationState(field.element);
    }
  }

  /**
   * Get current feedback state for a field
   */
  public getState(field: FieldInfo): FeedbackState | null {
    const context = this.contexts.get(field.element);
    return context?.state || null;
  }

  /**
   * Get current validation result for a field
   */
  public getValidation(field: FieldInfo): ValidationResult | null {
    const context = this.contexts.get(field.element);
    return context?.validation || null;
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<FeedbackConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Update dependent systems
    this.accessibilityManager.updateConfig(this.config.enableAccessibility);
    this.animations.updateConfig(this.config.enableAnimations);
    this.styles.updatePrefix(this.config.cssClassPrefix);
  }

  /**
   * Cleanup field feedback
   */
  public cleanupField(field: FieldInfo): void {
    const context = this.contexts.get(field.element);
    if (!context) {
      return;
    }

    // Clear all visual elements
    this.clearErrorMessage(context);
    this.clearErrorIcon(context);
    this.clearAnimations(field.element);

    // Remove accessibility attributes
    if (this.config.enableAccessibility) {
      this.accessibilityManager.cleanupField(field.element);
    }

    // Remove styling
    this.clearAllStyles(field.element);

    // Remove context
    this.contexts.delete(field.element);
  }

  /**
   * Initialize styles in DOM
   */
  private initializeStyles(): void {
    this.styles.injectStyles();
  }

  /**
   * Apply state-specific styles to element
   */
  private applyStateStyles(element: HTMLElement, state: FeedbackState): void {
    // Remove all state classes first
    Object.values(FeedbackState).forEach(s => {
      element.classList.remove(this.styles.getStateClass(s));
    });

    // Add current state class
    element.classList.add(this.styles.getStateClass(state));
  }

  /**
   * Clear all styles from element
   */
  private clearAllStyles(element: HTMLElement): void {
    Object.values(FeedbackState).forEach(state => {
      element.classList.remove(this.styles.getStateClass(state));
    });
  }

  /**
   * Show error message
   */
  private showErrorMessage(context: FeedbackContext, errors: string[]): void {
    if (errors.length === 0) {
      return;
    }

    // Get appropriate error message
    const message = this.errorMessages.getFieldTypeMessage(
      context.field.fieldType,
      errors[0],
      'error'
    );

    // Create or update message element
    const messageElement = this.createOrUpdateMessageElement(
      context,
      message,
      'error'
    );

    context.messageElement = messageElement;
  }

  /**
   * Show warning message
   */
  private showWarningMessage(context: FeedbackContext, warnings: string[]): void {
    if (warnings.length === 0) {
      return;
    }

    // Get appropriate warning message
    const message = this.errorMessages.getFieldTypeMessage(
      context.field.fieldType,
      warnings[0],
      'warning'
    );

    // Create or update message element
    const messageElement = this.createOrUpdateMessageElement(
      context,
      message,
      'warning'
    );

    context.messageElement = messageElement;
  }

  /**
   * Clear error message
   */
  private clearErrorMessage(context: FeedbackContext): void {
    if (context.messageElement) {
      context.messageElement.remove();
      context.messageElement = undefined;
    }
  }

  /**
   * Show error icon
   */
  private showErrorIcon(context: FeedbackContext): void {
    // Create or update icon element
    const iconElement = this.createOrUpdateIconElement(context, 'error');
    context.iconElement = iconElement;
  }

  /**
   * Clear error icon
   */
  private clearErrorIcon(context: FeedbackContext): void {
    if (context.iconElement) {
      context.iconElement.remove();
      context.iconElement = undefined;
    }
  }

  /**
   * Create or update message element
   */
  private createOrUpdateMessageElement(
    context: FeedbackContext,
    message: string,
    type: 'error' | 'warning' | 'success'
  ): HTMLElement {
    let messageElement = context.messageElement;

    if (!messageElement) {
      messageElement = document.createElement('div');
      messageElement.className = `${this.config.cssClassPrefix}-message ${this.config.cssClassPrefix}-message--${type}`;
      
      // Position the message
      this.positionMessage(messageElement, context.field.element);
    }

    messageElement.textContent = message;
    messageElement.setAttribute('role', type === 'error' ? 'alert' : 'status');

    // Set up accessibility
    if (this.config.enableAccessibility) {
      this.accessibilityManager.setupMessageElement(
        messageElement,
        context.field.element,
        type
      );
    }

    return messageElement;
  }

  /**
   * Create or update icon element
   */
  private createOrUpdateIconElement(
    context: FeedbackContext,
    type: 'error' | 'warning' | 'success'
  ): HTMLElement {
    let iconElement = context.iconElement;

    if (!iconElement) {
      iconElement = document.createElement('span');
      iconElement.className = `${this.config.cssClassPrefix}-icon ${this.config.cssClassPrefix}-icon--${type}`;
      
      // Position the icon
      this.positionIcon(iconElement, context.field.element);
    }

    // Set icon content based on type
    iconElement.textContent = this.getIconForType(type);
    iconElement.setAttribute('aria-hidden', 'true');

    return iconElement;
  }

  /**
   * Position message element relative to field
   */
  private positionMessage(messageElement: HTMLElement, fieldElement: HTMLElement): void {
    switch (this.config.messagePosition) {
      case 'below':
        fieldElement.parentNode?.insertBefore(
          messageElement,
          fieldElement.nextSibling
        );
        break;
      case 'above':
        fieldElement.parentNode?.insertBefore(messageElement, fieldElement);
        break;
      case 'inline':
        // Find or create inline container
        this.createInlineContainer(fieldElement).appendChild(messageElement);
        break;
      case 'tooltip':
        // Position as tooltip
        this.positionAsTooltip(messageElement, fieldElement);
        break;
    }
  }

  /**
   * Position icon element relative to field
   */
  private positionIcon(iconElement: HTMLElement, fieldElement: HTMLElement): void {
    // Create wrapper if needed
    const wrapper = this.ensureFieldWrapper(fieldElement);
    wrapper.appendChild(iconElement);
  }

  /**
   * Create inline container for messages
   */
  private createInlineContainer(fieldElement: HTMLElement): HTMLElement {
    let container = fieldElement.parentElement?.querySelector(
      `.${this.config.cssClassPrefix}-inline-container`
    ) as HTMLElement;

    if (!container) {
      container = document.createElement('div');
      container.className = `${this.config.cssClassPrefix}-inline-container`;
      fieldElement.parentNode?.insertBefore(container, fieldElement.nextSibling);
    }

    return container;
  }

  /**
   * Position element as tooltip
   */
  private positionAsTooltip(element: HTMLElement, anchor: HTMLElement): void {
    element.style.position = 'absolute';
    element.style.zIndex = '1000';
    
    const rect = anchor.getBoundingClientRect();
    element.style.top = `${rect.bottom + window.scrollY + 5}px`;
    element.style.left = `${rect.left + window.scrollX}px`;
    
    document.body.appendChild(element);
  }

  /**
   * Ensure field has wrapper for positioning elements
   */
  private ensureFieldWrapper(fieldElement: HTMLElement): HTMLElement {
    let wrapper = fieldElement.parentElement;
    
    if (!wrapper?.classList.contains(`${this.config.cssClassPrefix}-field-wrapper`)) {
      wrapper = document.createElement('div');
      wrapper.className = `${this.config.cssClassPrefix}-field-wrapper`;
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';
      
      fieldElement.parentNode?.insertBefore(wrapper, fieldElement);
      wrapper.appendChild(fieldElement);
    }

    return wrapper;
  }

  /**
   * Get icon character for feedback type
   */
  private getIconForType(type: 'error' | 'warning' | 'success'): string {
    switch (type) {
      case 'error':
        return '⚠️';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      default:
        return '';
    }
  }

  /**
   * Clear animations from element
   */
  private clearAnimations(element: HTMLElement): void {
    if (this.config.enableAnimations) {
      this.animations.clearAnimations(element);
    }
  }
}

/**
 * Global visual feedback instance
 */
let globalVisualFeedback: VisualFeedback | null = null;

/**
 * Get the global visual feedback instance
 */
export function getVisualFeedback(config?: FeedbackConfig): VisualFeedback {
  if (!globalVisualFeedback) {
    globalVisualFeedback = new VisualFeedback(config);
  }
  return globalVisualFeedback;
}

/**
 * Create a new visual feedback instance
 */
export function createVisualFeedback(config?: FeedbackConfig): VisualFeedback {
  return new VisualFeedback(config);
}